import { useState } from 'react';
import ImageLightbox from '@/components/ui/ImageLightbox';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import useTranslation from '@/hooks/useTranslation';

interface NotableVisual {
  url: string;
  type: string;
  caption?: string;
  respondentIndex?: number;
  conversationId?: string;
}

interface NotableVisualsGridProps {
  visuals: NotableVisual[];
}

function NotableVisualsGrid({ visuals }: NotableVisualsGridProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const { open: openTranscriptPanel } = useTranscriptPanelStore();
  const { lang } = useTranslation();

  const imageOnlyVisuals = visuals.filter((v) => v.type === 'image');

  // F1 / E5: localized respondent label with English fallback.
  const respondentLabel = (idx: number | string) =>
    (lang.get('msg.notableVisualsRespondent') || 'Respondent #{n}').replace(
      '{n}',
      String(idx),
    );

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visuals.map((visual, index) => (
          // E4 / audit F-8: prefer the visual.url (unique enough — same
          // file_id never appears twice in a single grid). Fall back to
          // index-suffixed key when two visuals legitimately share a url
          // (e.g. test data) — `key={url}` would collide and React would
          // re-use the wrong DOM node on prop change.
          <div
            key={`${visual.url}-${index}`}
            className="flex flex-col gap-2"
          >
            {visual.type === 'image' ? (
              <button
                type="button"
                onClick={() => {
                  const imageOnlyIndex =
                    visuals.slice(0, index + 1).filter((v) => v.type === 'image').length - 1;
                  setLightboxIndex(imageOnlyIndex);
                  setLightboxOpen(true);
                }}
                // E6 / audit F-8: focus-visible:ring instead of
                // focus:outline-none. Keyboard users now see a focus
                // ring; mouse users don't (Tailwind's focus-visible
                // pseudo only fires on keyboard nav).
                className="w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
              >
                <img
                  src={visual.url}
                  alt={
                    visual.caption ||
                    `Respondent ${visual.respondentIndex ?? '?'} image`
                  }
                  className="w-full h-64 object-cover rounded-lg border border-neutral-200 hover:opacity-90 transition-opacity cursor-pointer"
                />
              </button>
            ) : (
              <video
                src={visual.url}
                controls
                preload="metadata"
                className="w-full h-64 object-cover rounded-lg border border-neutral-200"
              />
            )}
            {visual.caption && (
              <p className="text-sm text-neutral-600 leading-relaxed">{visual.caption}</p>
            )}
            {visual.respondentIndex !== undefined && (
              visual.conversationId ? (
                <button
                  type="button"
                  onClick={() => openTranscriptPanel(visual.conversationId!)}
                  className="text-xs font-medium text-primary hover:underline w-fit focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {respondentLabel(visual.respondentIndex)}
                </button>
              ) : (
                <p className="text-xs text-neutral-400">{respondentLabel(visual.respondentIndex)}</p>
              )
            )}
          </div>
        ))}
      </div>

      <ImageLightbox
        images={imageOnlyVisuals.map((v) => ({
          url: v.url,
          caption: v.caption,
          respondent_index: v.respondentIndex,
        }))}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />
    </>
  );
}

export default NotableVisualsGrid;
